import React from 'react';
import './AutomotiveManualPage.css';

const SECTIONS = [
  { id: 'overview',      icon: '🏪', label: 'System Overview' },
  { id: 'roles',         icon: '👥', label: 'User Roles' },
  { id: 'setup',         icon: '⚙️',  label: 'First-Time Setup' },
  { id: 'products',      icon: '🔩', label: 'Products' },
  { id: 'opening-stock', icon: '📦', label: 'Opening Stock' },
  { id: 'purchases',     icon: '🛒', label: 'Purchases' },
  { id: 'sales',         icon: '💳', label: 'Sales (POS)' },
  { id: 'debts',         icon: '📋', label: 'Customer Credit' },
  { id: 'expenses',      icon: '💸', label: 'Expenses' },
  { id: 'reports',       icon: '📊', label: 'Reports' },
  { id: 'users',         icon: '👤', label: 'User Management' },
  { id: 'troubleshoot',  icon: '🔧', label: 'Troubleshooting' },
];

const WORKFLOW = [
  'Set up catalog', 'Enter opening stock', 'Record purchases', 'Sell at counter', 'Review reports',
];

function SectionHeader({ id, icon, num, title }) {
  return (
    <div id={id} className="auto-manual__section-header">
      <div className="auto-manual__section-icon-wrap">{icon}</div>
      <div className="auto-manual__section-meta">
        <span className="auto-manual__section-num">Step {num} of {SECTIONS.length}</span>
        <h2 className="auto-manual__section-title">{title}</h2>
      </div>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="auto-manual__steps">
      {items.map((item, i) => (
        <li key={i} className="auto-manual__step">
          <div className="auto-manual__step-left">
            <span className="auto-manual__step-num">{i + 1}</span>
            <span className="auto-manual__step-line" />
          </div>
          <div className="auto-manual__step-body">
            <p className="auto-manual__step-label">{item.label}</p>
            {item.desc && <p className="auto-manual__step-desc">{item.desc}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Tip({ children }) {
  return (
    <div className="auto-manual__tip">
      <span className="auto-manual__tip-icon">💡</span>
      <p className="auto-manual__tip-text">{children}</p>
    </div>
  );
}

function SubHead({ children }) {
  return <p className="auto-manual__subhead">{children}</p>;
}

const AutomotiveManualPage = ({ onNavigate }) => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="auto-manual page-bleed">

      {/* ── HERO HEADER ──────────────────────────────── */}
      <header className="auto-manual__header">
        <div className="auto-manual__header-inner">
          <span className="auto-manual__badge">📖 Automotive Products</span>
          <h1 className="auto-manual__title">User Manual</h1>
          <p className="auto-manual__subtitle">
            Complete guide for managing your spare parts shop — catalog, stock, purchases, POS sales, and reports.
          </p>
          <div className="auto-manual__workflow">
            {WORKFLOW.map((s, i) => (
              <div key={s} className="auto-manual__workflow-step">
                <span className="auto-manual__workflow-pill">
                  <span className="auto-manual__workflow-pill-num">{i + 1}</span>
                  {s}
                </span>
                {i < WORKFLOW.length - 1 && (
                  <span className="auto-manual__workflow-arrow">›</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ── TWO-COLUMN BODY ──────────────────────────── */}
      <div className="auto-manual__body">

        {/* ── STICKY SIDEBAR TOC ───────────────────── */}
        <aside className="auto-manual__sidebar">
          <nav className="auto-manual__toc" aria-label="Manual sections">
            <div className="auto-manual__toc-title">Contents</div>
            <ul className="auto-manual__toc-list">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="auto-manual__toc-link"
                    onClick={() => scrollTo(s.id)}
                  >
                    <span className="auto-manual__toc-link-num">{i + 1}</span>
                    <span className="auto-manual__toc-link-icon">{s.icon}</span>
                    <span>{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* ── MAIN CONTENT ─────────────────────────── */}
        <main className="auto-manual__content">

          {/* 1. OVERVIEW */}
          <section className="auto-manual__section">
            <SectionHeader id="overview" icon="🏪" num={1} title="System Overview" />
            <p className="auto-manual__section-lead">
              This system helps your spare parts shop track stock, manage sales, record supplier purchases, and generate reports — from any phone or computer.
            </p>
            <div className="auto-manual__table-wrap">
              <table className="auto-manual__table">
                <thead><tr><th>Feature</th><th>What it does for you</th></tr></thead>
                <tbody>
                  {[
                    ['Parts catalog',     'Register every spare part with category, brand, and unit once.'],
                    ['Opening stock',     'Enter how many of each part you have when you start.'],
                    ['Purchases',         'Record every supplier delivery — stock increases automatically.'],
                    ['POS Sales',         'Sell parts at the counter fast — stock deducted instantly.'],
                    ['Customer credit',   'Track parts sold on credit and record repayments.'],
                    ['Expenses',          'Record daily costs like transport, rent, and staff wages.'],
                    ['Reports',           'View daily, weekly, and monthly profit and stock status.'],
                    ['Low stock alerts',  'Get notified when a part is running out.'],
                  ].map(([f, d]) => (
                    <tr key={f}><td><strong>{f}</strong></td><td>{d}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="auto-manual__quick">
              {[
                ['→ Set up categories', 'auto-products-categories'],
                ['→ Add first spare part', 'auto-products-record'],
                ['→ Enter opening stock', 'auto-products-opening'],
              ].map(([label, key]) => (
                <button key={key} type="button" className="auto-manual__quick-btn"
                  onClick={() => onNavigate?.(key)}>{label}</button>
              ))}
            </div>
          </section>

          {/* 2. USER ROLES */}
          <section className="auto-manual__section">
            <SectionHeader id="roles" icon="👥" num={2} title="User Roles & Permissions" />
            <p className="auto-manual__section-lead">
              Every person gets a role controlling what they can see and do. The Owner assigns roles when adding staff.
            </p>
            <div className="auto-manual__table-wrap">
              <table className="auto-manual__table">
                <thead>
                  <tr><th>Feature</th><th>Owner</th><th>Manager</th><th>Staff</th></tr>
                </thead>
                <tbody>
                  {[
                    ['Dashboard',           true,  true,  true ],
                    ['Create sales (POS)',   true,  true,  true ],
                    ['Record purchases',     true,  true,  false],
                    ['Manage parts catalog', true,  true,  false],
                    ['Enter opening stock',  true,  true,  false],
                    ['View reports & profit',true,  true,  false],
                    ['Manage users',         true,  false, false],
                    ['Change settings',      true,  false, false],
                  ].map(([feature, o, m, s]) => (
                    <tr key={feature}>
                      <td>{feature}</td>
                      {[o, m, s].map((v, i) => (
                        <td key={i}>
                          {v ? <span className="auto-manual__check">✓</span>
                             : <span className="auto-manual__dash">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Tip>Add staff under Settings → Users. Each person should have their own login — never share passwords.</Tip>
          </section>

          {/* 3. FIRST-TIME SETUP */}
          <section className="auto-manual__section">
            <SectionHeader id="setup" icon="⚙️" num={3} title="First-Time Setup" />
            <p className="auto-manual__section-lead">
              Complete these steps in order before recording any sales or purchases.
            </p>
            <Steps items={[
              { label: 'Add product categories', desc: 'Products → Product categories. Add Engine Parts, Brakes, Electrical, Filters, etc.' },
              { label: 'Add brands',             desc: 'Products → Brands. Add Toyota, Bosch, Denso, or any brands you stock.' },
              { label: 'Add units',              desc: 'Products → Units. Add Piece (pcs), Set, Pair, Litre, etc.' },
              { label: 'Add suppliers',          desc: 'Purchases → New purchase order. Add importers and local distributors.' },
              { label: 'Register all spare parts', desc: 'Products → Record products. Add every part with name, category, brand, unit, cost, and selling price.' },
              { label: 'Enter opening stock',    desc: 'Products → Opening stock. Enter the quantity on hand for each part. This sets your starting inventory.' },
            ]} />
            <Tip>Do not start selling until all 6 steps are done. Doing opening stock after sales will make your stock history incorrect.</Tip>
          </section>

          {/* 4. PRODUCTS */}
          <section className="auto-manual__section">
            <SectionHeader id="products" icon="🔩" num={4} title="Products (Spare Parts)" />
            <p className="auto-manual__section-lead">
              Every part you buy or sell must be registered here first.
            </p>
            <SubHead>Adding a new spare part</SubHead>
            <Steps items={[
              { label: 'Go to Products → Record products' },
              { label: 'Enter part name',      desc: 'Be specific: Brake Pad Front (Toyota Corolla), not just Brake Pad.' },
              { label: 'Enter Part Code / SKU',desc: 'Your internal or supplier code e.g. BRK-001-F. Optional.' },
              { label: 'Select Category, Brand, Unit', desc: 'Must be created first under Product categories, Brands, and Units.' },
              { label: 'Enter Applicable Vehicles', desc: 'Optional e.g. Toyota Corolla 2015–2020. Helps staff find the right part.' },
              { label: 'Enter Cost Price (TZS)', desc: 'What you pay your supplier per unit.' },
              { label: 'Enter Selling Price (TZS)', desc: 'What you charge the customer at the counter.' },
              { label: 'Set Low Stock Alert',  desc: 'Get warned when stock falls below this number e.g. 5.' },
              { label: 'Click Save Part',      desc: 'Then go to Products → Opening stock to enter current quantity.' },
            ]} />
            <SubHead>Adding categories, brands & units quickly</SubHead>
            <Steps items={[
              { label: 'Categories', desc: 'Products → Product categories. Examples: Engine Parts, Brake System, Electrical, Suspension, Filters.' },
              { label: 'Brands',     desc: 'Products → Brands. Examples: Toyota, Bosch, NGK, Denso, Yamaha.' },
              { label: 'Units',      desc: 'Products → Units. Examples: pcs, set, pair, litre, gallon, box.' },
            ]} />
            <Tip>You can add new categories, brands, and units directly from the Record products form — type in the "Add new" field and click Add.</Tip>
          </section>

          {/* 5. OPENING STOCK */}
          <section className="auto-manual__section">
            <SectionHeader id="opening-stock" icon="📦" num={5} title="Opening Stock" />
            <p className="auto-manual__section-lead">
              Tell the system how many units of each part you have before starting sales or purchases.
            </p>
            <Steps items={[
              { label: 'Go to Products → Opening stock' },
              { label: 'Select the registered product', desc: 'Dropdown shows all registered parts. Category and unit appear automatically.' },
              { label: 'Enter the quantity on hand',    desc: 'Count your physical stock and enter the number.' },
              { label: 'Click Record Opening Stock',   desc: 'Stock updates immediately in inventory.' },
              { label: 'Repeat for all parts in your duka' },
            ]} />
            <Tip>Opening stock can only be entered once per product. Errors? Find the product and edit the quantity directly, or use a stock adjustment.</Tip>
          </section>

          {/* 6. PURCHASES */}
          <section className="auto-manual__section">
            <SectionHeader id="purchases" icon="🛒" num={6} title="Purchases — Receiving Stock" />
            <p className="auto-manual__section-lead">
              Record every supplier delivery. Stock increases automatically when a purchase is confirmed.
            </p>
            <Steps items={[
              { label: 'Go to Purchases → New purchase order' },
              { label: 'Select the supplier',  desc: 'Search by name. Add them if not yet in the system.' },
              { label: 'Set the date',         desc: 'The date goods were physically received at your shop.' },
              { label: 'Add parts',            desc: 'Click Add Item, select the part, enter quantity and cost price per unit.' },
              { label: 'Receive & track',      desc: 'Go to Purchases → Receive & tracking to confirm delivery and update stock.' },
            ]} />
            <Tip>Record purchases when goods arrive — not when you place the order. Stock only updates after you click Receive in the tracking section.</Tip>
          </section>

          {/* 7. SALES */}
          <section className="auto-manual__section">
            <SectionHeader id="sales" icon="💳" num={7} title="Sales — Counter POS" />
            <p className="auto-manual__section-lead">
              Record every sale. Stock is deducted automatically. Print a receipt for the customer.
            </p>
            <Steps items={[
              { label: 'Go to Sales → New sale' },
              { label: 'Select customer or Walk-in', desc: 'Search by name or phone. Use Walk-in Customer for quick cash sales.' },
              { label: 'Add parts being sold',       desc: 'Select each part — current stock and selling price appear automatically. Enter quantity.' },
              { label: 'Set payment status',         desc: 'Paid (cash/mobile), Partial, or Credit (customer pays later).' },
              { label: 'Click Save Sale',            desc: 'Stock is deducted. Receipt is ready to print.' },
              { label: 'Print receipt if needed',    desc: 'Click Print Invoice after saving.' },
            ]} />
            <Tip>You cannot sell more than available stock. The system shows an error if quantity exceeds what is in stock.</Tip>
          </section>

          {/* 8. CUSTOMER CREDIT */}
          <section className="auto-manual__section">
            <SectionHeader id="debts" icon="📋" num={8} title="Customer Credit (Debts)" />
            <p className="auto-manual__section-lead">
              Track parts sold on credit and record repayments when customers pay.
            </p>
            <Steps items={[
              { label: 'Record sale with payment status Credit', desc: 'Sale is saved and the customer\'s balance increases.' },
              { label: 'View credit customers',  desc: 'Sales → Credit customers — see all open balances.' },
              { label: 'Record a repayment',     desc: 'Click the customer → Add Payment → enter amount and method.' },
              { label: 'Balance updates automatically' },
            ]} />
            <Tip>Always save the customer's name and phone before creating a credit sale — makes it easy to follow up on unpaid balances.</Tip>
          </section>

          {/* 9. EXPENSES */}
          <section className="auto-manual__section">
            <SectionHeader id="expenses" icon="💸" num={9} title="Expenses" />
            <p className="auto-manual__section-lead">
              Record costs so profit reports are accurate. Owner and Manager only.
            </p>
            <Steps items={[
              { label: 'Go to Expenses → Add expense' },
              { label: 'Select expense type', desc: 'Owner/business, Staff wages, or Office/operating costs.' },
              { label: 'Enter amount, date, and description' },
              { label: 'Click Save', desc: 'Expense is deducted from your net profit in reports.' },
            ]} />
            <Tip>Record expenses daily. If you skip a week your profit report will look higher than it really is.</Tip>
          </section>

          {/* 10. REPORTS */}
          <section className="auto-manual__section">
            <SectionHeader id="reports" icon="📊" num={10} title="Reports & Analytics" />
            <p className="auto-manual__section-lead">
              Understand which parts sell most, what your profit is, and which stock needs reordering.
            </p>
            <div className="auto-manual__table-wrap">
              <table className="auto-manual__table">
                <thead><tr><th>Report</th><th>What you see</th></tr></thead>
                <tbody>
                  {[
                    ['Daily sales',    'All sales today — parts, quantities, amounts, profit.'],
                    ['Weekly sales',   'Sales trend over the past 7 days.'],
                    ['Monthly profit', 'Revenue vs expenses for the month.'],
                    ['Stock report',   'Current quantity and value for every spare part.'],
                  ].map(([r, d]) => (
                    <tr key={r}><td><strong>{r}</strong></td><td>{d}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="auto-manual__quick">
              {[
                ['Daily sales',    'report-daily'],
                ['Weekly sales',   'report-weekly'],
                ['Monthly profit', 'report-monthly'],
                ['Stock report',   'report-stock'],
              ].map(([label, key]) => (
                <button key={key} type="button" className="auto-manual__quick-btn"
                  onClick={() => onNavigate?.(key)}>→ {label}</button>
              ))}
            </div>
            <Tip>Check your daily report every evening. You will see which parts moved and whether your cash matches.</Tip>
          </section>

          {/* 11. USERS */}
          <section className="auto-manual__section">
            <SectionHeader id="users" icon="👤" num={11} title="User Management" />
            <p className="auto-manual__section-lead">
              Only the Owner can add or remove staff. Each person needs their own login.
            </p>
            <Steps items={[
              { label: 'Go to Branches & Team → Add user' },
              { label: 'Enter name, email, and phone number' },
              { label: 'Select a role', desc: 'Manager — full access except settings. Staff/Cashier — POS sales only.' },
              { label: 'Set a temporary password', desc: 'Tell them to change it on first login.' },
              { label: 'Click Save — they can log in immediately' },
            ]} />
            <Tip>If a staff member leaves, deactivate their account — do not delete it. All sales records are preserved.</Tip>
          </section>

          {/* 12. TROUBLESHOOTING */}
          <section className="auto-manual__section">
            <SectionHeader id="troubleshoot" icon="🔧" num={12} title="Troubleshooting" />
            <p className="auto-manual__section-lead">Common problems and how to fix them.</p>
            {[
              { q: 'A part is not showing in the POS sales form',
                a: 'Check that the product has stock. Parts with zero stock do not appear. Enter opening stock or record a purchase to add units.' },
              { q: 'Stock did not increase after entering a purchase',
                a: 'Go to Purchases → Receive & tracking and confirm the delivery. Stock only increases after clicking Receive.' },
              { q: 'Opening stock shows 0 after entering it',
                a: 'Refresh the page. If still 0, use a stock adjustment — opening stock can only be entered once per product.' },
              { q: 'I cannot log in',
                a: 'Check your email and password (case-sensitive). Use Forgot Password on the login page.' },
              { q: 'A report is showing wrong totals',
                a: 'Check that opening stock was entered before any sales were made and verify the date range filter.' },
              { q: 'The selling price in POS is wrong',
                a: 'Go to Products → Products list, find the part, and edit the selling price.' },
            ].map(({ q, a }) => (
              <div key={q} className="auto-manual__trouble">
                <p className="auto-manual__trouble-q">❓ {q}</p>
                <p className="auto-manual__trouble-a">→ {a}</p>
              </div>
            ))}
            <Tip>Still need help? Contact your system administrator with a description of the issue and the page you were on.</Tip>
          </section>

        </main>
      </div>
    </div>
  );
};

export default AutomotiveManualPage;
